import type { MeshMode } from '@/api/generated/types.gen';
import type { SimulationType } from './faceLabels';

/** Layers are supported by CFD and by wrap CHT's fluid contact zones. */
export function simulationSupportsBoundaryLayers(
  simulationType: string,
  wrapInput = false,
): boolean {
  return simulationType === 'cfd' || (simulationType === 'cht' && wrapInput);
}

// The physics each saved mode belongs to; `undefined` for an unset or unknown
// mode. Old drafts can still hold the pre-ENG-2323 'cht' (ENG-3103): CHT.
function physicsOfMode(mode: string | undefined): SimulationType | undefined {
  if (mode === 'fea') return 'fea';
  if (mode === 'cfd_internal' || mode === 'cfd_external') return 'cfd';
  if (mode === 'cht' || mode === 'cht_internal' || mode === 'cht_external')
    return 'cht';
  return undefined;
}

// What each FEA | CFD | CHT button writes as the mode (MeshSettingsPanel):
// CFD opens on Outside flow, CHT on ambient cooling.
const PHYSICS_DEFAULT_MODE: Record<SimulationType, MeshMode> = {
  cfd: 'cfd_external',
  fea: 'fea',
  cht: 'cht_external',
};

/**
 * A saved mode from another physics is never a choice — the buttons write both
 * together — so it becomes that physics' default (ENG-3150). A mode of the same
 * physics (including the old 'cht') and an unset mode come back unchanged.
 */
export function domainModeForPhysics<M extends string | undefined>(
  simulationType: SimulationType,
  mode: M,
): M | MeshMode {
  const owner = physicsOfMode(mode);
  return owner === undefined || owner === simulationType
    ? mode
    : PHYSICS_DEFAULT_MODE[simulationType];
}
